<template>
    <div class="[ flex flex-col gap-4 ]" v-bind="$attrs">
        <div class="[ flex justify-between items-center gap-4 ]">
            <div class="[ flex-1 flex ]">
                <h2 class="[ font-bold ]">Api Keys</h2>
            </div>
            <ButtonComponent label="Create Api Key" outlined @click="openCreateDialog">
                <template #icon>
                    <JBIcon name="add" />
                </template>
            </ButtonComponent>
        </div>

        <DataTable :value="data" :loading="loading">
            <template #empty>
                <Empty icon="unavailable" title="No Api Key"
                    message="Create an Api Key to access the Jack Burton API." />
            </template>
            <Column field="id" header="Id">
                <template #body="{ data }: { data: ApiKey }">
                    <div>{{ data.id }}</div>
                </template>
            </Column>
            <Column field="name" header="Name">
                <template #body="{ data }: { data: ApiKey }">
                    <div>{{ data.name }}</div>
                </template>
            </Column>
            <Column field="dateCreated" header="Date Created">
                <template #body="{ data }: { data: ApiKey }">
                    <div>{{ formatDate(data.dateCreated!) }}</div>
                </template>
            </Column>
            <Column field="dateAccessed" header="Last used on">
                <template #body="{ data }: { data: ApiKey }">
                    <div>{{ data.dateAccessed ? formatDate(data.dateAccessed) : 'Never' }}</div>
                </template>
            </Column>
            <Column field="dateRevoked" header="Revoked on">
                <template #body="{ data }: { data: ApiKey }">
                    <div>{{ data.isRevoked ? formatDate(data.dateRevoked!) : "N/A" }}</div>
                </template>
            </Column>
            <Column>
                <template #body="{ data }: { data: ApiKey }">
                    <ButtonComponent v-if="!data.isRevoked" outlined label="Revoke" @click="confirmDelete(data.id!)" />
                </template>
            </Column>
        </DataTable>
    </div>
    <JBDialog v-model:visible="showConfirm" header="Revoke api key">
        <div>Are you sure you want to revoke this key?</div>
        <template #footer>
            <div class="[ flex gap-3 justify-end ]">
                <ButtonComponent label="Revoke" @click="commitDelete" :loading="isDeleting" />
            </div>
        </template>
    </JBDialog>
    <JBDialog v-model:visible="showCreateDialog" header="Create new Api Key">
        <div class="[ flex flex-col h-full gap-4 p-x-3 ]">
            <GridForm size="full">
                <GridFormField id="name" label="Name">
                    <template #input="{ id }">
                        <InputText :id="id" v-model="name" placeholder="API Key Name" />
                    </template>
                </GridFormField>
            </GridForm>
            <div class="[ flex justify-end ]">
                <ButtonComponent label="Create" :loading="isCreating"
                    @click="commit" :disabled="!validForm" />
            </div>
        </div>
    </JBDialog>
    <JBDialog v-model:visible="showSecretDialog" header="Api Key Secret">
        <div class="[ flex flex-col h-full gap-4 p-x-3 ]">
            <div>
                <span>This is the only time you will be able to see this secret. Make sure to save it somewhere safe.</span>
                <pre>
                    {{ newApiKeySecret }}
                </pre>
            </div>
            <div class="[ flex justify-end ]">
                <ButtonComponent label="I saved it" @click="closeSecret"  />
            </div>
        </div>
    </JBDialog>
</template>

<script setup lang="ts">
import { Column, DataTable, Button as ButtonComponent, InputText } from "primevue";
import { useApi, useApiLoader } from '@/services/api';
import { computed, onMounted, ref } from 'vue';
import GridForm from "@/components/ui/GridForm.vue";
import GridFormField from "@/components/ui/GridFormField.vue";
import JBDialog from "@/components/ui/modals/JBDialog.vue";
import Empty from "@/components/ui/Empty.vue";
import { ApiKey } from "@/services/api/sdk";
import { format } from "date-fns";
import { useRouter } from "vue-router";

defineOptions({
    inheritAttrs: false,
});

definePage({
    meta: { title: 'API Keys', icon: 'user-story' },
});

// REFS

const showCreateDialog = ref(false);
const showSecretDialog = ref(false);
const showConfirm = ref(false);
const selectedId = ref(-1);
const name = ref('');

// COMPUTED 

const validForm = computed(() => {
    return name.value;
});

// API

const api = useApi();

const { data, load, loading } = useApiLoader(async () => {
    const res = await api.getApiKeys();
    return res.data!;
}, []);

const { loading: isCreating, load: createApiKey, data: newApiKeySecret } = useApiLoader(async () => {
    const res = await api.createApiKey({
        createApiKeyRequest: {
            name: name.value,
        },
    });
    return res.data;
}, null);

const { loading: isDeleting, load: revokeApiKey } = useApiLoader(async () => {
    await api.revokeApiKey({
        apiKeyId: selectedId.value,
    });
}, null);

// FUNCTION

function confirmDelete(id: number) {
    selectedId.value = id;
    showConfirm.value = true;
}

async function commit() {
    if (await createApiKey()) {
        showCreateDialog.value = false;
        showSecretDialog.value = true;
        await load();
    }
}

async function commitDelete() {
    await revokeApiKey();
    showConfirm.value = false;
    await load();
}

function openCreateDialog() {
    name.value = '';
    showCreateDialog.value = true;
}

function closeSecret() {
    showSecretDialog.value = false;
    newApiKeySecret.value = null;
}

function formatDate(date: Date) {
    return format(date, 'yyyy-MM-dd HH:mm:ss');
}

onMounted(() => {
    load();
});

</script>